import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { useTenant } from '../../contexts/TenantContext'

const UserSettings = () => {
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const theme = tenant?.settings?.theme || tenant?.theme || 'default'
  const isRedTheme = theme === 'throwball' || tenant?.slug === 'tfi'

  const [activeTab, setActiveTab] = useState('password')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState({})
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      const response = await api.get('/user/profile')
      if (response.data.success) {
        setProfile(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const response = await api.put('/user/profile', {
        association_name: profile.association_name,
        president: profile.president,
        secretary: profile.secretary,
        official_contact: profile.official_contact,
        official_email: profile.official_email,
        address: profile.address,
        city: profile.city,
        postal_code: profile.postal_code
      })
      if (response.data.success) {
        alert('Profile updated successfully')
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      alert(error.response?.data?.message || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()

    if (passwords.new_password.length < 6) {
      alert('New password must be at least 6 characters')
      return
    }

    if (passwords.new_password !== passwords.new_password_confirmation) {
      alert('New password and confirmation do not match')
      return
    }

    setSaving(true)
    try {
      const response = await api.post('/user/change-password', passwords)
      if (response.data.success) {
        alert('Password changed successfully')
        setPasswords({ current_password: '', new_password: '', new_password_confirmation: '' })
      }
    } catch (error) {
      console.error('Error changing password:', error)
      alert(error.response?.data?.message || 'Failed to change password')
    } finally {
      setSaving(false)
    }
  }

  const tabs = [
    { id: 'password', label: 'Change Password', icon: 'fas fa-lock' },
    { id: 'account', label: 'Account Info', icon: 'fas fa-user-cog' }
  ]

  return (
    <div className="user-settings-page">
      {/* Header */}
      <div className="settings-header">
        <div className="header-left">
          <div>
            <h1><i className="fas fa-cog"></i> Settings</h1>
            <p>Manage your association and account settings</p>
          </div>
        </div>
        <button className="back-button" onClick={() => navigate('/user/dashboard')}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading settings...</p>
        </div>
      ) : (
        <div className="settings-content">
          {/* Association Info Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="settings-form">
              <div className="form-section">
                <h3><i className="fas fa-building"></i> Association Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Association Name</label>
                    <input
                      type="text"
                      value={profile.association_name || ''}
                      onChange={(e) => setProfile({ ...profile, association_name: e.target.value })}
                      placeholder="Enter association name"
                    />
                  </div>
                  <div className="form-group">
                    <label>President Name</label>
                    <input
                      type="text"
                      value={profile.president || ''}
                      onChange={(e) => setProfile({ ...profile, president: e.target.value })}
                      placeholder="Enter president name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Secretary Name</label>
                    <input
                      type="text"
                      value={profile.secretary || ''}
                      onChange={(e) => setProfile({ ...profile, secretary: e.target.value })}
                      placeholder="Enter secretary name"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3><i className="fas fa-phone-alt"></i> Contact Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Official Contact</label>
                    <input
                      type="tel"
                      value={profile.official_contact || ''}
                      onChange={(e) => setProfile({ ...profile, official_contact: e.target.value })}
                      placeholder="Enter contact number"
                      maxLength={10}
                    />
                  </div>
                  <div className="form-group">
                    <label>Official Email</label>
                    <input
                      type="email"
                      value={profile.official_email || ''}
                      onChange={(e) => setProfile({ ...profile, official_email: e.target.value })}
                      placeholder="Enter official email"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Address</label>
                    <textarea
                      value={profile.address || ''}
                      onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                      placeholder="Enter address"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={profile.city || ''}
                      onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                      placeholder="Enter city"
                    />
                  </div>
                  <div className="form-group">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      value={profile.postal_code || ''}
                      onChange={(e) => setProfile({ ...profile, postal_code: e.target.value })}
                      placeholder="Enter postal code"
                      maxLength={6}
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-check"></i> Save Changes</>}
                </button>
              </div>
            </form>
          )}

          {/* Change Password Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordChange} className="settings-form">
              <div className="form-section">
                <h3><i className="fas fa-lock"></i> Change Password</h3>
                <div className="password-info">
                  <i className="fas fa-info-circle"></i>
                  <p>Password must be at least 6 characters long.</p>
                </div>
                <div className="form-grid single-column">
                  <div className="form-group">
                    <label>Current Password</label>
                    <div className="password-input">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwords.current_password}
                        onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                        placeholder="Enter current password"
                        required
                      />
                      <button type="button" className="toggle-password" onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}>
                        <i className={`fas fa-eye${showPasswords.current ? '-slash' : ''}`}></i>
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>New Password</label>
                    <div className="password-input">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwords.new_password}
                        onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                        placeholder="Enter new password"
                        required
                        minLength={6}
                      />
                      <button type="button" className="toggle-password" onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}>
                        <i className={`fas fa-eye${showPasswords.new ? '-slash' : ''}`}></i>
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <div className="password-input">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwords.new_password_confirmation}
                        onChange={(e) => setPasswords({ ...passwords, new_password_confirmation: e.target.value })}
                        placeholder="Confirm new password"
                        required
                        minLength={6}
                      />
                      <button type="button" className="toggle-password" onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}>
                        <i className={`fas fa-eye${showPasswords.confirm ? '-slash' : ''}`}></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? <><i className="fas fa-spinner fa-spin"></i> Changing...</> : <><i className="fas fa-key"></i> Change Password</>}
                </button>
              </div>
            </form>
          )}

          {/* Account Info Tab */}
          {activeTab === 'account' && (
            <div className="settings-form">
              <div className="form-section">
                <h3><i className="fas fa-user-cog"></i> Account Information</h3>
                <div className="info-cards">
                  <div className="info-card">
                    <div className="info-icon"><i className="fas fa-user"></i></div>
                    <div className="info-details">
                      <span className="info-label">Username</span>
                      <span className="info-value">{profile.username || '-'}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fas fa-envelope"></i></div>
                    <div className="info-details">
                      <span className="info-label">Email</span>
                      <span className="info-value">{profile.email || '-'}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fas fa-map-marker-alt"></i></div>
                    <div className="info-details">
                      <span className="info-label">State</span>
                      <span className="info-value">{profile.state?.name || '-'}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fas fa-shield-alt"></i></div>
                    <div className="info-details">
                      <span className="info-label">Account Status</span>
                      <span className={`info-value status-${(profile.status || '').toLowerCase()}`}>
                        {(profile.status || '').charAt(0).toUpperCase() + (profile.status || '').slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fas fa-user-tag"></i></div>
                    <div className="info-details">
                      <span className="info-label">Role</span>
                      <span className="info-value">{profile.role || 'User'}</span>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon"><i className="fas fa-calendar-plus"></i></div>
                    <div className="info-details">
                      <span className="info-label">Account Created</span>
                      <span className="info-value">{profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN') : '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        .user-settings-page {
          padding: 2rem;
          background: #f9fafb;
          min-height: 100vh;
        }

        .settings-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          background: white;
          padding: 1.5rem 2rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          border-top: 4px solid var(--primary-color, #022a5e);
        }

        .header-left {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }

        .back-button {
          padding: 0.6rem 1.2rem;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          transition: all 0.2s;
          color: var(--primary-color, #022a5e);
        }

        .back-button:hover {
          background: rgba(2, 42, 94, 0.05);
          border-color: var(--primary-color, #022a5e);
        }

        .settings-header h1 {
          margin: 0 0 0.25rem;
          font-size: 1.5rem;
          color: var(--primary-color, #022a5e);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .settings-header p {
          margin: 0;
          color: #6b7280;
          font-size: 0.9rem;
        }

        .settings-tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .settings-tab {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.9rem;
          color: #6b7280;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .settings-tab:hover {
          border-color: var(--primary-color, #022a5e);
          color: var(--primary-color, #022a5e);
        }

        .settings-tab.active {
          background: var(--primary-color, #022a5e);
          border-color: var(--primary-color, #022a5e);
          color: white;
        }

        .settings-content {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .settings-form {
          padding: 2rem;
        }

        .form-section {
          margin-bottom: 2rem;
        }

        .form-section:last-child {
          margin-bottom: 0;
        }

        .form-section h3 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #111827;
          margin: 0 0 1.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #f3f4f6;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-section h3 i {
          color: var(--primary-color, #022a5e);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }

        .form-grid.single-column {
          grid-template-columns: 1fr;
          max-width: 500px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-weight: 600;
          font-size: 0.85rem;
          color: #374151;
        }

        .form-group input,
        .form-group textarea {
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: all 0.2s;
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary-color, #022a5e);
          box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
        }

        .password-input {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-input input {
          width: 100%;
          padding-right: 3rem;
        }

        .toggle-password {
          position: absolute;
          right: 0.75rem;
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 0.25rem;
          font-size: 1rem;
        }

        .toggle-password:hover {
          color: #6b7280;
        }

        .password-info {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          background: #dbeafe;
          border-left: 4px solid #3b82f6;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .password-info i {
          color: #1e40af;
          flex-shrink: 0;
        }

        .password-info p {
          margin: 0;
          color: #1e3a8a;
          font-size: 0.85rem;
        }

        .form-actions {
          padding-top: 1.5rem;
          border-top: 2px solid #f3f4f6;
          display: flex;
          justify-content: flex-end;
        }

        .btn-save {
          padding: 0.75rem 2rem;
          background: var(--primary-color, #022a5e);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-save:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .btn-save:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Account Info Cards */
        .info-cards {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }

        .info-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem;
          background: #f9fafb;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          transition: all 0.2s;
        }

        .info-card:hover {
          border-color: var(--primary-color, #022a5e);
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .info-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-color, #022a5e);
          font-size: 1.1rem;
          flex-shrink: 0;
          border: 1px solid #e5e7eb;
        }

        .info-details {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          min-width: 0;
        }

        .info-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .info-value {
          font-size: 0.95rem;
          font-weight: 600;
          color: #111827;
          word-break: break-word;
        }

        .info-value.status-active {
          color: #059669;
        }

        .info-value.status-pending {
          color: #d97706;
        }

        .info-value.status-suspended {
          color: #dc2626;
        }

        .loading-state {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .loading-state i {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          color: var(--primary-color, #022a5e);
        }

        .loading-state p {
          color: #6b7280;
          font-size: 1rem;
        }

        @media (max-width: 768px) {
          .user-settings-page {
            padding: 1rem;
          }

          .settings-header {
            padding: 1rem;
          }

          .header-left {
            flex-direction: column;
            align-items: flex-start;
          }

          .settings-tabs {
            flex-direction: column;
          }

          .settings-form {
            padding: 1.25rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .info-cards {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}

export default UserSettings
